import {BaseCard} from "../../Components/Cards/BaseCard";
import {useBoundStore} from "../../Store/store";
import {useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {AdminApplicationCard} from "../../Components/Cards/AdminApplicationCard";
import {SelectInput} from "../../Components/Form/SelectInput";
import {DateTimePickerInput} from "../../Components/Form/DateTimePickerInput";
import {ControlledSwitch} from "../../Components/Form/ControlledSwitch";
import * as React from "react";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useTranslation} from "react-i18next";
import {BlackButton} from "../../Components/Buttons/BlackButton";
import CheckStats from "../../Assets/images/illustrations/check_stats.png";


export const Applications = () => {
    const navigate = useNavigate();
    const {t} = useTranslation();

    const [loading, setLoading] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);

    const getAllApplicationsFromFreelancers = useBoundStore(state => state.getAllApplicationsFromFreelancers);
    const applications = useBoundStore(state => state.applications);
    const applicationsMetaData = useBoundStore(state => state.applicationsMetaData);
    const freelancers = useBoundStore(state => state.freelancers);
    const getAllFreelancers = useBoundStore(state => state.getAllFreelancers);
    const customers = useBoundStore((state) => state.customers)
    const getAllCustomers = useBoundStore((state) => state.getAllCustomers);

    useEffect(() => {
        getAllFreelancers(null,null,true);
    }, [getAllFreelancers]);

    useEffect(() => {
        getAllCustomers(null, null, {nopaginate: true});
    }, [getAllCustomers]);

    const schema = yup.object({
        freelancer_id: yup.mixed().nullable(),
        customer_id: yup.string().nullable(),
        status: yup.string().nullable(),
        only_in_progress: yup.boolean().nullable(),
        start_time: yup.string().nullable(),
        end_time: yup.string().nullable(),
    }).required();

    const startOfToday = React.useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const {control, setValue, reset, getValues, formState:{errors}, watch} = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            customer_id: null,
            start_time: startOfToday,
            end_time: null,
            freelancer_id: null,
            status: null,
            only_in_progress: false,
        }
    });

    const customerId = watch('customer_id');
    const freelancerId = watch('freelancer_id');
    const status = watch('status');
    const onlyInProgress = watch('only_in_progress');
    const startTime = watch('start_time');
    const endTime = watch('end_time');

    useEffect(() => {
        setCurrentPage(1);
    }, [customerId, startTime, endTime, freelancerId, status, onlyInProgress]);

    useEffect(() => {
        setLoading(true);

        (
            async () => {
                await getAllApplicationsFromFreelancers(
                    {
                        page: currentPage,
                        customer_id: customerId,
                        freelancer_id: freelancerId,
                        status,
                        start_time: startTime,
                        end_time: endTime,
                        in_progress: onlyInProgress ? 1 : undefined,
                    }
                )

                setLoading(false);
            }
        )()
    }, [getAllApplicationsFromFreelancers,currentPage, customerId, startTime, endTime, freelancerId, status, onlyInProgress]);

    const goToNextPage = () => {
        if(canGoToNextPage()) {
            setCurrentPage(currentPage + 1);
        }
    }

    const goToPreviousPage = () => {
        if(canGoToPrevious()) {
            setCurrentPage(currentPage - 1);
        }
    }

    const getPageCountFromMetaData = () => {
        return applicationsMetaData?.last_page;
    }

    const canGoToNextPage = () => {
        return applicationsMetaData?.last_page > currentPage;
    };

    const canGoToPrevious = () => {
        return currentPage > 1;
    };

    return (
        <div className={'grid grid-cols-1 gap-4'}>
            <BaseCard>
                <div className={'flex flex-row justify-end mb-2'}>
                    <BlackButton onClick={() => {
                        reset({
                            customer_id: null,
                            freelancer_id: null,
                            status: null,
                            start_time: startOfToday,
                            end_time: null,
                            only_in_progress: false,
                        })
                    }}>{t('general.actions.clear_filters')}</BlackButton>
                </div>
                <div className={'grid grid-cols-1 md:grid-cols-5 gap-4'}>
                    <SelectInput
                        name={'customer_id'}
                        options={customers.map((customer) => {
                            return {
                                id: customer?.id,
                                value: customer?.id,
                                label: `${customer?.company?.name ?? `${customer?.firstname} ${customer?.lastname}`}`
                            }
                        })}
                        control={control}
                        value={getValues('customer_id') ?? []}
                        setValue={setValue}
                        placeholder={t('general.fields.client')}
                        translationIndex={'shifts.client'}
                        clearable={true}
                    />

                    <SelectInput
                        name={'freelancer_id'}
                        options={freelancers.map((freelancer) => {
                            return {
                                id: freelancer?.id,
                                value: freelancer?.id,
                                label: `${freelancer?.firstname} ${freelancer?.lastname}`
                            }
                        })}
                        control={control}
                        value={getValues('freelancer_id') ?? []}
                        setValue={setValue}
                        placeholder={t('general.fields.freelancer')}
                        translationIndex={'general.fields.freelancer'}
                        clearable={true}
                    />

                    <SelectInput
                        name={'status'}
                        options={[
                            {id: 'applied', value: 'applied', label: t('general.status.applied')},
                            {id: 'accepted', value: 'accepted', label: t('general.status.accepted')},
                            {id: 'rejected', value: 'rejected', label: t('general.status.rejected')},
                            {id: 'other_accepted', value: 'other_accepted', label: t('general.status.other_accepted')},
                        ]}
                        control={control}
                        value={getValues('status') ?? []}
                        setValue={setValue}
                        placeholder={t('general.fields.status')}
                        translationIndex={'general.labels.status'}
                        clearable={true}
                    />

                    <DateTimePickerInput
                        name={'start_time'}
                        setValue={setValue}
                        control={control}
                        currentValue={getValues('start_time')}
                        translationIndex={'general.fields.start_time'}
                        errors={errors}
                        isClearable={true}
                    />
                    <DateTimePickerInput
                        name={'end_time'}
                        setValue={setValue}
                        control={control}
                        currentValue={getValues('end_time')}
                        translationIndex={'general.fields.end_time'}
                        errors={errors}
                        isClearable={true}
                    />
                </div>
                <div className={'mt-4'}>
                    <ControlledSwitch
                        control={control}
                        name={'only_in_progress'}
                        getValues={getValues}
                        errors={errors}
                        setValue={setValue}
                        translationIndex={'Alleen in behandeling'}
                    />
                </div>
            </BaseCard>

            <BaseCard>
                <div className={'grid grid-cols-1 md:grid-cols-3 gap-4'}>
                    {
                        loading && (
                            <div className="flex justify-center items-center col-span-3">
                                <div
                                    className="animate-spin rounded-full mx-auto h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
                            </div>
                        )
                    }

                    {
                        !loading && applications.map((application, idx, index) => {
                            return (
                                <AdminApplicationCard
                                    application={application}
                                    key={idx}
                                    imageOverride={CheckStats}
                                    onClickButton={() => {
                                        navigate(`/admin/applications/${application?.id}`)
                                    }}
                                />
                            )
                        })
                    }
                </div>


                <div className="flex sm:flex-row flex-col w-full mt-8 items-end justify-end gap-2 text-xs">
                    <div className="flex gap-2">
                        <button
                            className={`${
                                !canGoToPrevious()
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-200 hover:cursor-pointer bg-gray-100'
                            } rounded p-1`}
                            onClick={() => setCurrentPage(1)}
                            disabled={!canGoToPrevious()}
                        >
                            <span className="w-5 h-5">{'<<'}</span>
                        </button>
                        <button
                            className={`${
                                !canGoToPrevious()
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-200 hover:curstor-pointer bg-gray-100'
                            } rounded p-1`}
                            onClick={() => {
                                goToPreviousPage()
                            }}
                            disabled={!canGoToPrevious()}
                        >
                            <span className="w-5 h-5">{'<'}</span>
                        </button>
                        <span className="flex items-center gap-1">
                            {currentPage} / {getPageCountFromMetaData()}
                        </span>
                        <button
                            className={`${
                                !canGoToNextPage()
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-200 hover:cursor-pointer bg-gray-100'
                            } rounded p-1`}
                            onClick={() => {
                                goToNextPage()
                            }}
                            disabled={!canGoToNextPage()}
                        >
                            <span className="w-5 h-5">{'>'}</span>
                        </button>
                        <button
                            className={`${
                                !canGoToNextPage()
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-200 hover:cursor-pointer bg-gray-100'
                            } rounded p-1`}
                            onClick={() => setCurrentPage(getPageCountFromMetaData())}
                            disabled={!canGoToNextPage()}
                        >
                            <span className="w-5 h-5">{'>>'}</span>
                        </button>
                    </div>
                </div>
            </BaseCard>
        </div>
    )
}